import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiMessageSquare, FiX, FiCopy } from "react-icons/fi";
import { AiOutlineEdit } from "react-icons/ai";
import ENDPOINTS from "../../api/endpoint";

export default function SearchModal({ isOpen, onClose, onConversationClick }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [promptLibraryResults, setPromptLibraryResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setSearchQuery("");
      setSearchResults([]);
      setPromptLibraryResults([]);
    }
  }, [isOpen]);

  // Search conversations
  useEffect(() => {
    const searchConversations = async () => {
      if (!searchQuery.trim()) {
        setSearchResults([]);
        setPromptLibraryResults([]);
        return;
      }

      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const token = user?.access_token;
      const companyId = user?.company_id;
      const userId = user?.user_id || user?.id;

      if (!companyId || !userId || !token) {
        console.error("Missing credentials");
        return;
      }

      setLoading(true);
      try {
        const res = await fetch(
          ENDPOINTS.SEARCH_CONVERSATIONS(companyId, userId),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              query: searchQuery,
              limit: 10000,
            }),
          }
        );

        const data = await res.json();

        if (!res.ok) {
          console.error("Search failed:", data.detail);
          setSearchResults([]);
          setPromptLibraryResults([]);
          return;
        }

        // Handle different response formats for conversations
        if (Array.isArray(data)) {
          setSearchResults(data);
        } else if (data.results && Array.isArray(data.results)) {
          setSearchResults(data.results);
        } else if (data.conversations && Array.isArray(data.conversations)) {
          setSearchResults(data.conversations);
        } else {
          setSearchResults([]);
        }

        // Handle prompt_library_results - only show items with non-null template_preview
        if (data.prompt_library_results && Array.isArray(data.prompt_library_results)) {
          const filtered = data.prompt_library_results.filter(
            (p) => p.template_preview != null && p.template_preview !== ""
          );
          setPromptLibraryResults(filtered);
        } else {
          setPromptLibraryResults([]);
        }
      } catch (err) {
        console.error("Error searching conversations:", err);
        setSearchResults([]);
        setPromptLibraryResults([]);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(() => {
      searchConversations();
    }, 500); // Debounce for 500ms

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleNewChat = () => {
    onClose();
    localStorage.removeItem("conversation_id");
    navigate("/home", { state: { refresh: Date.now() } });
  };

  const handleCopyPrompt = async (prompt) => {
    try {
      await navigator.clipboard.writeText(prompt.template_preview);
      onClose();
    } catch (err) {
      console.error("Failed to copy prompt:", err);
    }
  };

  const handleConversationClick = (conversation) => {
    onClose();

    // Get conversation_id - search results use conversation_id, sidebar uses id
    const conversationId = conversation.conversation_id || conversation.id;

    if (!conversationId) {
      console.error("No conversation ID found");
      return;
    }

    // Set active conversation
    localStorage.setItem("conversation_id", conversationId);
    if (window.updateSidebarActiveConversation) {
      window.updateSidebarActiveConversation(conversationId);
    }

    // Call the parent handler if provided
    if (onConversationClick) {
      onConversationClick(conversation);
    } else {
      // Navigate to home with conversation ID in state
      navigate("/home", {
        state: {
          refresh: Date.now(),
          conversationId: conversationId,
          fromSearch: true,
        },
      });

      // Load the conversation data after a short delay
      setTimeout(() => {
        if (window.loadConversationData) {
          window.loadConversationData(conversationId);
        }
      }, 100);
    }
  };

  // Group conversations by date
  const groupByDate = (conversations) => {
    // Ensure conversations is an array
    if (!Array.isArray(conversations)) {
      return {
        last7Days: [],
        last30Days: [],
        older: [],
      };
    }

    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const groups = {
      last7Days: [],
      last30Days: [],
      older: [],
    };

    conversations.forEach((conv) => {
      const convDate = new Date(conv.created_at || conv.timestamp);

      if (convDate >= sevenDaysAgo) {
        groups.last7Days.push(conv);
      } else if (convDate >= thirtyDaysAgo) {
        groups.last30Days.push(conv);
      } else {
        groups.older.push(conv);
      }
    });

    return groups;
  };

  const groupedResults = groupByDate(searchResults);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 pt-16 px-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-bg-darkSecondary rounded-2xl w-full max-w-2xl shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search chats..."
              className="w-full bg-gray-100 dark:bg-bg-dark rounded-lg px-4 py-3 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-gray-200"
              autoFocus
            />
            <FiMessageSquare
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
          </div>
          <button
            onClick={onClose}
            className="ml-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <FiX className="text-gray-600 dark:text-gray-400" size={20} />
          </button>
        </div>

        {/* New Chat Button */}
        {!searchQuery && (
          <div className="p-4">
            <button
              onClick={handleNewChat}
              className="w-full flex items-center gap-3 bg-gray-100 dark:bg-bg-dark hover:bg-gray-200 dark:hover:bg-gray-700 px-4 py-3 rounded-lg transition-colors text-left"
            >
              <AiOutlineEdit
                className="text-indigo-600 dark:text-indigo-400"
                size={20}
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                New chat
              </span>
            </button>
          </div>
        )}

        {/* Search Results */}
        <div className="max-h-[60vh] overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              Searching...
            </div>
          ) : searchQuery ? (
            <>
              {/* Prompt Library Results */}
              {promptLibraryResults.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                    Prompt Library
                  </h3>
                  <ul className="space-y-1">
                    {promptLibraryResults.map((prompt) => (
                      <li
                        key={prompt.prompt_id}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm text-gray-700 dark:text-gray-300"
                      >
                        <span className="flex-1 truncate">{prompt.prompt_name}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyPrompt(prompt);
                          }}
                          className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors flex-shrink-0"
                          title="Copy template"
                        >
                          <FiCopy className="text-gray-500 dark:text-gray-400" size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Previous 7 Days */}
              {groupedResults.last7Days.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                    Previous 7 Days
                  </h3>
                  <ul className="space-y-1">
                    {groupedResults.last7Days.map((conv) => (
                      <li
                        key={conv.conversation_id || conv.id}
                        onClick={() => handleConversationClick(conv)}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors text-sm text-gray-700 dark:text-gray-300"
                      >
                        <div className="w-6 h-6 border-2 border-gray-400 rounded-full flex-shrink-0" />
                        <span className="flex-1 truncate">
                          {conv.title || "Untitled Conversation"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Previous 30 Days */}
              {groupedResults.last30Days.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                    Previous 30 Days
                  </h3>
                  <ul className="space-y-1">
                    {groupedResults.last30Days.map((conv) => (
                      <li
                        key={conv.conversation_id || conv.id}
                        onClick={() => handleConversationClick(conv)}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors text-sm text-gray-700 dark:text-gray-300"
                      >
                        <div className="w-6 h-6 border-2 border-gray-400 rounded-full flex-shrink-0" />
                        <span className="flex-1 truncate">
                          {conv.title || "Untitled Conversation"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Older */}
              {groupedResults.older.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                    Older
                  </h3>
                  <ul className="space-y-1">
                    {groupedResults.older.map((conv) => (
                      <li
                        key={conv.conversation_id || conv.id}
                        onClick={() => handleConversationClick(conv)}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors text-sm text-gray-700 dark:text-gray-300"
                      >
                        <div className="w-6 h-6 border-2 border-gray-400 rounded-full flex-shrink-0" />
                        <span className="flex-1 truncate">
                          {conv.title || "Untitled Conversation"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* No Results */}
              {!loading &&
                searchQuery &&
                groupedResults.last7Days.length === 0 &&
                groupedResults.last30Days.length === 0 &&
                groupedResults.older.length === 0 &&
                promptLibraryResults.length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No conversations or prompts found matching "{searchQuery}"
                  </div>
                )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
